import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { API } from "@/lib/auth";
import { BLOG_PATH } from "@/lib/blogConfig";
import { ArrowLeft, Clock, Download, Calendar } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SITE } from "@/lib/site";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Sanitize blog HTML to defend against XSS. Allow safe formatting tags
  // including <figure>/<img> for PDF-extracted images (data: URLs ok).
  const sanitizedBody = useMemo(() => {
    const dirty = post?.content_html || "";
    const clean = DOMPurify.sanitize(dirty, {
      USE_PROFILES: { html: true },
      ADD_TAGS: ["figure", "figcaption"],
      ADD_ATTR: ["loading", "target", "rel"],
      // Allow data: URLs on images (PDF-extracted base64) but block on links
      ALLOWED_URI_REGEXP:
        /^(?:(?:(?:f|ht)tps?|mailto|tel|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    });
    return { __html: clean };
  }, [post]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/posts/${slug}`);
        if (!cancelled) {
          setPost(data);
          document.title = `${data.title} — J.J. Botelho`;
          // Inject Article JSON-LD schema
          const schema = {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: data.title,
            description: data.excerpt,
            image: data.featured_image || undefined,
            datePublished: data.published_at,
            dateModified: data.updated_at,
            author: {
              "@type": "Person",
              name: "J.J. Botelho",
              url: "https://www.jj-botelho.com/",
            },
            articleSection: data.category,
            keywords: (data.tags || []).join(", "),
          };
          let tag = document.getElementById("post-jsonld");
          if (!tag) {
            tag = document.createElement("script");
            tag.id = "post-jsonld";
            tag.type = "application/ld+json";
            document.head.appendChild(tag);
          }
          tag.text = JSON.stringify(schema);
        }
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      const tag = document.getElementById("post-jsonld");
      if (tag) tag.remove();
      document.title = "J.J. Botelho — Realtor & Mortgage Advisor | Okaloosa County, FL";
    };
  }, [slug]);

  return (
    <main data-testid="blog-post-page" className="bg-bone min-h-screen">
      <Navbar />

      {loading ? (
        <div className="pt-44 pb-24 text-center text-muted2 text-sm">Loading…</div>
      ) : notFound || !post ? (
        <div className="pt-44 pb-24 text-center" data-testid="post-not-found">
          <h1 className="font-display text-ink text-4xl">Post not found.</h1>
          <Link to={BLOG_PATH} className="mt-6 inline-block text-brass underline">
            Back to the blog
          </Link>
        </div>
      ) : (
        <>
          {/* Header */}
          <section className="relative pt-36 pb-12 md:pt-44 md:pb-16 bg-ink text-white overflow-hidden">
            <div className="absolute -top-32 -right-32 w-[450px] h-[450px] rounded-full bg-brass/10 blur-3xl pointer-events-none" />
            <div className="relative max-w-4xl mx-auto px-6 md:px-12">
              <Link
                to={BLOG_PATH}
                data-testid="post-back-btn"
                className="inline-flex items-center gap-2 text-white/70 hover:text-brass text-sm transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all posts
              </Link>
              <p className="overline text-brass mt-8">{post.category}</p>
              <h1
                data-testid="post-title"
                className="mt-5 font-display text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
              >
                {post.title}
              </h1>
              {post.excerpt && (
                <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed">
                  {post.excerpt}
                </p>
              )}
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/70 tracking-widest uppercase">
                <span className="inline-flex items-center gap-2">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(post.published_at)}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Clock className="w-3.5 h-3.5" />
                  {post.read_time_minutes} min read
                </span>
                <span>By J.J. Botelho</span>
                {post.has_pdf && (
                  <a
                    href={`${API}/posts/${post.slug}/pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="post-download-pdf"
                    className="inline-flex items-center gap-2 text-brass hover:text-white border border-brass/40 hover:border-white rounded-full px-3 py-1 ml-auto"
                  >
                    <Download className="w-3.5 h-3.5" />
                    PDF
                  </a>
                )}
              </div>
            </div>
          </section>

          {/* Featured image */}
          {post.featured_image && (
            <div className="max-w-4xl mx-auto px-6 md:px-12 -mt-8 md:-mt-12 relative z-10">
              <img
                src={post.featured_image}
                alt={post.title}
                data-testid="post-hero-image"
                className="w-full aspect-[16/9] object-cover rounded-xl border border-border2 shadow-xl bg-sand"
              />
            </div>
          )}

          {/* Body */}
          <article
            className="post-body max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24 text-text2"
            data-testid="post-body"
            dangerouslySetInnerHTML={sanitizedBody}
          />

          {/* Tags + CTA */}
          <section className="max-w-3xl mx-auto px-6 md:px-12 pb-20">
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-10">
                {post.tags.map((t) => (
                  <span
                    key={t}
                    className="text-[11px] tracking-widest uppercase bg-sand text-ink border border-border2 rounded-full px-3 py-1"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
            <div className="border-t border-border2 pt-10">
              <p className="overline text-ink">Questions?</p>
              <h3 className="mt-4 font-display text-ink text-2xl md:text-3xl tracking-tight">
                Want to talk through this in more detail?
              </h3>
              <p className="mt-3 text-text2/80 leading-relaxed">
                Call {SITE.phone} or email me — I'll respond within one business day.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={SITE.phoneHref}
                  className="inline-flex items-center gap-2 bg-ink text-white px-5 py-3 rounded-full text-sm font-medium hover:bg-slate2"
                >
                  Call {SITE.phone}
                </a>
                <a
                  href="/#contact"
                  className="inline-flex items-center gap-2 border border-ink text-ink px-5 py-3 rounded-full text-sm font-medium hover:bg-ink hover:text-white"
                >
                  Open contact form
                </a>
                <Link
                  to={BLOG_PATH}
                  className="inline-flex items-center gap-2 text-ink text-sm border-b border-ink pb-0.5 px-2"
                >
                  More posts
                </Link>
              </div>
            </div>
          </section>
        </>
      )}

      <Footer />
    </main>
  );
}
